import type { LimitationDefinition, LimitationValue } from "src/shared/api-types/limitations";

import { limitationTemplates, type LimitationTemplate } from "./limitationTemplates";
import type { LimitationFormValues } from "./types";

export const createCodeFromName = (name: string, fallback = "limitation"): string => {
  const code = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return code || fallback;
};

export const createFormValuesFromTemplate = (
  template: LimitationTemplate,
  name: string,
  sortOrder: number,
): LimitationFormValues => ({
  ...toFormValues(),
  templateKey: template.key,
  code: template.code,
  name,
  type: template.type,
  operator: template.operator,
  unit: template.unit,
  sortOrder,
  options: template.values.map((value, index) => ({
    value,
    sortOrder: index,
    archived: false,
  })),
});

export const getDefaultValueForType = (type: LimitationDefinition["type"]): LimitationValue => {
  if (type === "number") return 0;
  if (type === "boolean") return true;
  return "";
};

export const getDefaultOptionForType = (
  type: LimitationDefinition["type"],
): LimitationFormValues["options"][number] => ({
  value: getDefaultValueForType(type),
  sortOrder: 0,
  archived: false,
});

export const toFormValues = (definition?: LimitationDefinition): LimitationFormValues => {
  if (!definition) {
    return {
      code: "",
      name: "",
      templateKey: "wind",
      type: "number",
      operator: "<",
      unit: "",
      sortOrder: 0,
      options: [getDefaultOptionForType("number")],
    };
  }

  return {
    code: definition.code,
    name: definition.name,
    templateKey: limitationTemplates.find((template) => template.code === definition.code)?.key ?? "custom",
    type: definition.type,
    operator: definition.operator,
    unit: definition.unit ?? "",
    sortOrder: definition.sortOrder,
    options: definition.options.map((option) => ({
      id: option.id,
      value: option.value,
      sortOrder: option.sortOrder,
      archived: option.archived,
      usageCount: option.usageCount,
    })),
  };
};

export const normalizeOptionValue = (type: LimitationDefinition["type"], value: LimitationValue): LimitationValue => {
  if (type === "number") return value === "" ? "" : Number(value);
  if (type === "boolean") return value === true || value === "true";
  return String(value);
};

export const normalizeOptionOrder = (options: LimitationFormValues["options"]): LimitationFormValues["options"] =>
  options.map((option, index) => ({ ...option, sortOrder: index }));

export const getBooleanOptions = (options: LimitationFormValues["options"] = []): LimitationFormValues["options"] => {
  const existingTrueOption = options.find((option) => option.value === true || option.value === "true");
  return [
    {
      ...existingTrueOption,
      value: true,
      sortOrder: 0,
      archived: existingTrueOption?.archived ?? false,
    },
  ];
};

export const ensureGeneratedCodes = (values: LimitationFormValues): LimitationFormValues => {
  const definitionCode = values.code || createCodeFromName(values.name);
  const options = values.type === "boolean" ? getBooleanOptions(values.options) : values.options;

  return {
    ...values,
    code: definitionCode,
    options: normalizeOptionOrder(options),
  };
};
